<script>
import _ from 'lodash'

import localesDictionary from '@/locales/dictionary.json'
import constants from '@/constants'

const NOT_FOUND_CODE = 404

export default {
  localStorage: {
    token: '',
  },

  methods: {
    getToken() {
      return this.$store.getters.token
    },
    getAccount() {
      return this.$store.getters.account
    },
    getAccountId() {
      return this.$store.getters.accountId
    },
    getOrganizationId() {
      return this.$store.getters.organizationId
    },
    getOrganization() {
      return this.$store.getters.organization
    },
    getTimezone() {
      return this.$store.getters.timezone
    },

    showLoader() {
      this.$store.dispatch('showLoader')
    },

    hideLoader() {
      this.$store.dispatch('hideLoader')
    },

    showAlert(type, message) {
      this.$store.dispatch('showAlert', {
        type: type,
        message: message,
        status: true,
        keepAlive: false,
      })

      setTimeout(this.hideAlert, 5000)
    },

    hideAlert() {
      this.alertData = {
        type: '',
        message: '',
        status: false,
      }

      this.$store.dispatch('hideAlert')
    },

    showError(message) {
      this.$store.dispatch('addNotificationToAlerts', {
        type: 'danger',
        message: message,
        status: true,
        keepAlive: false,
      })
    },

    showSuccess(message) {
      this.$store.dispatch('addNotificationToAlerts', {
        type: 'success',
        message: message,
        status: true,
        keepAlive: false,
      })

      setTimeout(this.hideAlert, 5000)
    },

    showWarning(message) {
      this.$store.dispatch('addNotificationToAlerts', {
        type: 'warning',
        message: message,
        status: true,
        keepAlive: false,
      })
    },

    showInfo(message) {
      this.$store.dispatch('addNotificationToAlerts', {
        type: 'info',
        message: message,
        status: true,
        keepAlive: false,
      })
    },

    navigateTo(location) {
      // When try to push same route as where we are, "NavigationDuplicated" exception is thrown.
      // This catch block is to prevent this.
      this.$root.$router.push(location).catch(() => {})
    },

    openInNewTab(location) {
      const url = this.$router.resolve(location).href
      window.open(url, '_blank')
    },

    navigateToExternalRoute(path) {
      window.location.href = path
    },

    openExternalRouteInNewTab(path) {
      window.open(path, '_blank')
    },

    goBack() {
      this.$root.$router.go(-1)
    },

    getRouteParams() {
      return this.$route?.params
    },

    async replaceRoute(routeParams) {
      // When updating query params a "NavigationDuplicated" exception is thrown.
      // This catch block is to prevent this, but it also could lead to some different exceptions being ignored.
      await this.$router.replace(routeParams).catch(() => {})
    },

    getRouteQuery() {
      return this.$route?.query
    },

    getURLParam(param) {
      return this.$route.params[param]
    },

    pathIs(path) {
      return this.$route.matched[0].path === path
    },

    pathIsNot(path) {
      return this.$route.matched[0].path !== path
    },

    getFullName(user) {
      if (!user) return ''

      try {
        const fullName = `${user?.displayname ?? ''} ${user?.familyname ??
          ''}`.trim()

        return fullName || (user?.email ?? '')
      } catch (error) {
        return user?.email ?? ''
      }
    },

    getLocaleWithoutDialect(locale = '') {
      return locale?.split(/[_-]/)[0]
    },

    getLanguages() {
      let settedLanguages = [...constants.supportedLanguages]

      if (!this.$isFeatureEnabled(this.$featuresKeys?.dialect)) {
        settedLanguages = _.uniq(
          settedLanguages.map(language =>
            this.getLocaleWithoutDialect(language)
          )
        )
      }

      return settedLanguages.map(language => {
        const gotLanguage = {
          value: language,
          name: language,
          key: language,
        }
        if (localesDictionary['language-names'][language]) {
          gotLanguage.name = localesDictionary['language-names'][language][0]
        } else if (localesDictionary['primary-dialects'][language]) {
          const langWithDialect =
            localesDictionary['primary-dialects'][language]
          gotLanguage.name =
            localesDictionary['language-names'][langWithDialect][0]
        }

        return gotLanguage
      })
    },

    getLanguagesForTranslationKeys() {
      const allLanguages = Object.keys(localesDictionary['primary-dialects'])

      return allLanguages.map(language => {
        const gotLanguage = {
          value: language,
          name: language,
          key: language,
        }

        if (localesDictionary['language-names'][language]) {
          gotLanguage.name = localesDictionary['language-names'][language][1]
        } else if (localesDictionary['primary-dialects'][language]) {
          const langWithDialect =
            localesDictionary['primary-dialects'][language]
          gotLanguage.name =
            localesDictionary['language-names'][langWithDialect][1]
        }
        return gotLanguage
      })
    },

    getRevLanguageName(rev) {
      const availableLanguages = this.getLanguagesForTranslationKeys()

      return (
        availableLanguages
          .find(lang => rev?.lang === lang.value)
          ?.name.toLowerCase() ??
        rev?.lang ??
        ''
      )
    },

    checkNotFoundError(error) {
      const status = error?.response?.status
      if (status === NOT_FOUND_CODE) return this.navigateTo('/404')
    },

    handleRequestError(err) {
      const status = err?.response?.status
      const errorCode = err?.response?.data?.errorCode
      const errorDetails = err?.response?.data?.message ?? err?.message

      console.trace(err)

      if (this.$constants.generalErrors.includes(errorCode)) {
        return this.showError(this.$t(`error.generalErrors.${errorCode}`))
      }
      if (this.$constants.supportedErrors.includes(status)) {
        return this.showError(
          this.$t(`error.generic_errors.${status}`, { details: errorDetails })
        )
      }
      return this.showError(
        this.$t('error.generic_errors.500', {
          details: errorDetails,
        })
      )
    },

    pascalCase(string) {
      return _.upperFirst(_.camelCase(string))
    },

    isObjectNotEmpty(obj) {
      const values = _.compact(_.flattenDeep(_.values(obj)))
      return values.length > 0
    },

    getInitialSelectedTableColumns(tableHeaders = [], selectedColumns = []) {
      return tableHeaders.map(header => {
        return {
          ...header,
          show: selectedColumns.includes(header.value),
        }
      })
    },

    getFileExtension(filename) {
      if (!filename) return ''
      const lastDotIndex = filename.lastIndexOf('.')
      if (lastDotIndex === -1) return ''
      return filename.substring(lastDotIndex).toLowerCase()
    },

    normalizeFileExtension(filenameOrExtension) {
      if (!filenameOrExtension) return null
      
      const hasExtensionDot = filenameOrExtension.startsWith('.')
      const extension = hasExtensionDot 
        ? filenameOrExtension 
        : this.getFileExtension(filenameOrExtension)
      
      return extension?.toLowerCase()
    },

    getFileIconByExtension(filenameOrExtension) {
      const ext = this.normalizeFileExtension(filenameOrExtension)
      
      switch (ext) {
        case '.csv':
        case '.xls':
        case '.xlsx':
          return 'mdi-file-excel-outline'
        case '.doc':
        case '.docx':
          return 'mdi-file-word-outline'
        case '.ppt':
        case '.pptx':
          return 'mdi-file-powerpoint-outline'
        case '.zip':
        case '.rar':
          return 'mdi-folder-zip-outline'
        case '.pdf':
          return 'mdi-file-pdf-box'
        default:
          return 'mdi-file-document-outline'
      }
    },
  },
}
</script>
